"use client";

import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import type { AppLocale } from "@/core/i18n/config";
import { normalizeAppError } from "@/shared/lib/async-result";
import { getSessionOptions } from "../../../data/repositories/booking.repository";
import type { ConsultationMode, HealerSessionOption } from "../../../types/booking";
import { useBookingStepperStore } from "../../states/booking-stepper.store";
import { MODE_ICON, MODE_LABEL } from "./shared";
function getErrorMessage(error: unknown): string {
  return normalizeAppError(error).message;
}
export function Step2ModeDuration({
  healerId,
  locale
}: {
  healerId: string;
  locale: AppLocale;
}) {
  const isHindi = locale === "hi";
  const [options, setOptions] = useState<HealerSessionOption[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [selectedMode, setSelectedMode] = useState<ConsultationMode | null>(null);
  const setSessionOption = useBookingStepperStore(state => state.setSessionOption);
  const previousStep = useBookingStepperStore(state_0 => state_0.previousStep);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getSessionOptions(healerId);
        if (!cancelled) setOptions(data);
      } catch (caughtError) {
        if (!cancelled) setError(getErrorMessage(caughtError));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [healerId]);
  function handleContinue() {
    const option = options?.find(entry => entry.id === selectedOptionId);
    if (!option || !selectedMode) return;
    setSessionOption(option, selectedMode);
  }
  return <div>
      <h2 className="font-heading text-xl font-semibold text-[#0E2A3A]">
        {isHindi ? "मोड और अवधि चुनें" : "Choose mode and duration"}
      </h2>
      <p className="mt-1 text-sm text-slate-500">
        {isHindi ? "यह हीलर द्वारा दिए जाने वाले सत्र विकल्प हैं।" : "These are the session options this healer offers."}
      </p>

      {loading && <div className="mt-8 flex items-center justify-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          {isHindi ? "विकल्प लोड हो रहे हैं..." : "Loading options..."}
        </div>}

      {!loading && error && <p className="mt-6 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">{error}</p>}

      {!loading && !error && options && options.length === 0 && <p className="mt-6 text-sm text-slate-500">
          {isHindi ? "इस हीलर के लिए अभी कोई सत्र विकल्प उपलब्ध नहीं है।" : "No session options are available for this healer right now."}
        </p>}

      {!loading && !error && options && options.length > 0 && <div className="mt-6 space-y-3">
          {options.map(option_0 => {
        const isSelectedOption = selectedOptionId === option_0.id;
        return <div key={option_0.id} className={`rounded-2xl border p-4 transition ${isSelectedOption ? "border-[#155A7D] bg-[#155A7D]/5" : "border-[#EAE5DA]"}`}>
                <button type="button" onClick={() => {
            setSelectedOptionId(option_0.id);
            setSelectedMode(option_0.consultationModes.includes(selectedMode ?? "VIDEO") ? selectedMode : option_0.consultationModes[0] ?? null);
          }} className="flex w-full items-center justify-between text-left">
                  <div>
                    <p className="font-semibold text-[#0E2A3A]">
                      {option_0.durationMinutes} {isHindi ? "मिनट" : "min"}
                      {option_0.isDefault && <span className="ml-2 rounded-full bg-[#C06A42]/10 px-2 py-0.5 text-[10px] font-semibold text-[#C06A42]">
                          {isHindi ? "लोकप्रिय" : "Popular"}
                        </span>}
                    </p>
                    <p className="mt-1 text-sm text-slate-500">₹{option_0.price}</p>
                  </div>
                </button>

                {isSelectedOption && <div className="mt-3 flex flex-wrap gap-2">
                    {option_0.consultationModes.map(mode => {
              const Icon = MODE_ICON[mode];
              const isActive = selectedMode === mode;
              return <button key={mode} type="button" onClick={() => setSelectedMode(mode)} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${isActive ? "border-[#155A7D] bg-[#155A7D] text-white" : "border-[#EAE5DA] text-slate-500 hover:border-[#155A7D]/40"}`}>
                          <Icon className="h-3.5 w-3.5" />
                          {isHindi ? MODE_LABEL[mode].hi : MODE_LABEL[mode].en}
                        </button>;
            })}
                  </div>}
              </div>;
      })}
        </div>}

      <div className="mt-8 flex gap-3">
        <button type="button" onClick={previousStep} className="rounded-full border border-[#EAE5DA] px-5 py-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50">
          {isHindi ? "वापस" : "Back"}
        </button>
        <button type="button" disabled={!selectedOptionId || !selectedMode} onClick={handleContinue} className="flex-1 rounded-full bg-[#155A7D] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#0E4663] disabled:cursor-not-allowed disabled:opacity-50">
          {isHindi ? "जारी रखें" : "Continue"}
        </button>
      </div>
    </div>;
}